/* ==========================================================================
   Featured customer reviews
   Image first proof slider. Reuses the CSV data layer created by
   customer-reviews.js (window.CustomerReviewsCache) so both sections share a
   single fetch and a single parse per page.

   Looping is done with a cloned strip. Full copies of the review set sit on
   both sides of the real one, so the track is periodic: shifting scrollLeft by
   exactly one set width redraws an identical picture. Walking off the last
   card carries on into a copy of the first, and the strip is silently shifted
   back into the middle once the scroll settles.
   ========================================================================== */

(function () {
  'use strict';

  var STAR_PATH =
    'M316.9 18C311.6 7 300.4 0 288.1 0s-23.4 7-28.8 18L195 150.3 51.4 171.5c-12 1.8-22 10.2-25.7 21.7s-.7 24.2 7.9 32.7L137.8 329 113.2 474.7c-2 12 3 24.2 12.9 31.3s23 8 33.8 2.3l128.3-68.5 128.3 68.5c10.8 5.7 23.9 4.9 33.8-2.3s14.9-19.3 12.9-31.3L438.5 329 542.7 225.9c8.6-8.5 11.7-21.2 7.9-32.7s-13.7-19.9-25.7-21.7L381.2 150.3 316.9 18z';

  if (window.FeaturedCustomerReviews && window.FeaturedCustomerReviews.initAll) {
    window.FeaturedCustomerReviews.initAll(document);
    return;
  }

  var reducedMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* How many slide widths away the focus effect fades out over. */
  var FOCUS_FALLOFF = 2;

  /* Quiet window after the last scroll event before the strip is recentred.
     Long enough that a smooth scroll or a touch flick has finished moving. */
  var SETTLE_DELAY = 140;

  /* Transitions come off for the single frame a lap change happens in, so no
     card animates towards the value it is already being given. Injected rather
     than left in the stylesheet so the loop cannot be half installed. */
  var JUMP_STYLE_ID = 'fcr-jump-style';

  function ensureJumpStyle() {
    var style;

    if (document.getElementById(JUMP_STYLE_ID)) return;

    style = document.createElement('style');
    style.id = JUMP_STYLE_ID;
    style.textContent = '.fcr__track.is-jumping,.fcr__track.is-jumping *{transition:none!important;}';
    (document.head || document.documentElement).appendChild(style);
  }

  /* ----------------------------------------------------------- primitives */

  function toString(value) {
    return value == null ? '' : String(value);
  }

  function trim(value) {
    return toString(value).trim();
  }

  function toBool(value) {
    return value === true || value === 'true' || value === '1' || value === 'yes';
  }

  function toNumber(value, fallback) {
    var parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function mod(value, size) {
    return ((value % size) + size) % size;
  }

  function el(tag, className) {
    var node = document.createElement(tag);
    if (className) {
      node.className = className;
    }
    return node;
  }

  function textEl(tag, className, text) {
    var node = el(tag, className);
    node.textContent = text;
    return node;
  }

  function empty(node) {
    while (node && node.firstChild) {
      node.removeChild(node.firstChild);
    }
  }

  function removeAll(nodes) {
    Array.prototype.slice.call(nodes).forEach(function (node) {
      if (node.parentNode) {
        node.parentNode.removeChild(node);
      }
    });
  }

  function svgFrom(markup) {
    var host = document.createElement('span');
    host.innerHTML = markup;
    return host.firstElementChild;
  }

  function safeMediaUrl(value) {
    var url = trim(value);
    var lower = url.replace(/\s+/g, '').toLowerCase();

    if (!url) return '';
    if (lower.indexOf('javascript:') === 0 || lower.indexOf('data:') === 0 || lower.indexOf('vbscript:') === 0) {
      return '';
    }
    if (/^(https?:)?\/\//i.test(url) || url.charAt(0) === '/' || /^(files|cdn|assets)\//i.test(url)) {
      return url;
    }
    return '';
  }

  function normalizeName(value) {
    return toString(value)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function formatAverage(value) {
    return (Math.round(value * 10) / 10).toFixed(1);
  }

  function formatDate(timestamp, format, locale) {
    var fmt = format || 'none';
    var options;
    var diffDays;
    var unit;
    var value;

    if (!timestamp || fmt === 'none') return '';

    if (fmt === 'relative') {
      diffDays = Math.floor((Date.now() - timestamp) / 86400000);
      if (diffDays < 1) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return diffDays + ' days ago';
      if (diffDays < 30) {
        value = Math.floor(diffDays / 7);
        unit = 'week';
      } else if (diffDays < 365) {
        value = Math.floor(diffDays / 30);
        unit = 'month';
      } else {
        value = Math.floor(diffDays / 365);
        unit = 'year';
      }
      return value + ' ' + unit + (value === 1 ? '' : 's') + ' ago';
    }

    if (fmt === 'short') {
      options = { day: '2-digit', month: '2-digit', year: 'numeric' };
    } else if (fmt === 'long') {
      options = { day: 'numeric', month: 'long', year: 'numeric' };
    } else {
      options = { day: 'numeric', month: 'short', year: 'numeric' };
    }

    try {
      return new Intl.DateTimeFormat(locale || 'en-AU', options).format(new Date(timestamp));
    } catch (error) {
      return new Intl.DateTimeFormat('en-AU', options).format(new Date(timestamp));
    }
  }

  /* -------------------------------------------------------------- data layer */

  function whenDataLayerReady() {
    return new Promise(function (resolve, reject) {
      var start = Date.now();

      (function poll() {
        var cache = window.CustomerReviewsCache;
        if (cache && typeof cache.load === 'function') {
          resolve(cache);
          return;
        }
        if (Date.now() - start > 8000) {
          reject(new Error('Customer reviews data layer is unavailable.'));
          return;
        }
        window.setTimeout(poll, 60);
      })();
    });
  }

  function hasImage(review) {
    return !!(review.image_urls && review.image_urls.length && safeMediaUrl(review.image_urls[0]));
  }

  function hasMedia(review) {
    return hasImage(review) || !!safeMediaUrl(review.video_url);
  }

  function parseCuratedEntries(raw) {
    return toString(raw)
      .split(/[\n\r,]+/)
      .map(trim)
      .filter(Boolean);
  }

  /* Curated picks are matched on author name. An optional "Name :: keyword"
     form disambiguates repeat reviewers by a word from their review. */
  function pickCurated(reviews, entries, options) {
    var used = {};
    var picked = [];

    entries.forEach(function (entry) {
      var parts = entry.split('::');
      var name = normalizeName(parts[0]);
      var keyword = trim(parts[1]).toLowerCase();
      var matches;

      if (!name) return;

      matches = reviews.filter(function (review) {
        if (used[review._index]) return false;
        if (normalizeName(review.author_name) !== name) return false;
        if (keyword && toString(review.review_text).toLowerCase().indexOf(keyword) < 0) return false;
        return true;
      });

      if (!matches.length) return;

      matches.sort(function (a, b) {
        var aMedia = hasMedia(a) ? 0 : 1;
        var bMedia = hasMedia(b) ? 0 : 1;
        if (aMedia !== bMedia) return aMedia - bMedia;
        if (b.rating !== a.rating) return b.rating - a.rating;
        return b.date_timestamp - a.date_timestamp;
      });

      used[matches[0]._index] = true;
      picked.push(matches[0]);
    });

    if (options.requireMedia) {
      picked = picked.filter(hasMedia);
    }

    return picked;
  }

  function pickAuto(reviews, options) {
    var list = reviews.filter(function (review) {
      if (options.requireMedia && !hasMedia(review)) return false;
      if (options.requireText && !trim(review.review_text)) return false;
      if (review.rating < options.minRating) return false;
      return true;
    });

    if (options.sort === 'rating') {
      list.sort(function (a, b) {
        if (b.rating !== a.rating) return b.rating - a.rating;
        return b.date_timestamp - a.date_timestamp;
      });
    } else if (options.sort === 'oldest') {
      list.sort(function (a, b) {
        return a.date_timestamp - b.date_timestamp;
      });
    } else if (options.sort === 'longest') {
      list.sort(function (a, b) {
        return toString(b.review_text).length - toString(a.review_text).length;
      });
    } else if (options.sort === 'random') {
      list = list
        .map(function (review) {
          return { review: review, key: Math.random() };
        })
        .sort(function (a, b) {
          return a.key - b.key;
        })
        .map(function (item) {
          return item.review;
        });
    }
    /* 'newest' is the order filterReviews already returns. */

    return list;
  }

  function selectReviews(reviews, settings) {
    var mode = settings.fcrMode || 'curated';
    var options = {
      requireMedia: toBool(settings.fcrRequireMedia),
      requireText: toBool(settings.fcrRequireText),
      minRating: toNumber(settings.fcrMinRating, 0),
      sort: settings.fcrSort || 'newest'
    };
    var entries = parseCuratedEntries(settings.fcrCurated);
    var picked = [];
    var seen = {};

    if (mode === 'curated' || mode === 'curated_then_auto') {
      picked = pickCurated(reviews, entries, options);
    }

    if (mode === 'auto' || mode === 'curated_then_auto') {
      picked.forEach(function (review) {
        seen[review._index] = true;
      });
      pickAuto(reviews, options).forEach(function (review) {
        if (!seen[review._index]) {
          picked.push(review);
        }
      });
    }

    /* A curated list that matches nothing must never leave a blank slider. */
    if (mode === 'curated' && !picked.length) {
      picked = pickAuto(reviews, options);
    }

    return picked.slice(0, Math.max(1, toNumber(settings.fcrMaxSlides, 8)));
  }

  /* ---------------------------------------------------------------- markup */

  function starsNode(rating, ariaLabel) {
    var wrap = el('span', 'fcr-stars');
    var rounded = Math.round(rating);
    var index;
    var star;

    wrap.setAttribute('role', 'img');
    wrap.setAttribute('aria-label', ariaLabel || formatAverage(rating) + ' out of 5 stars');

    for (index = 1; index <= 5; index += 1) {
      star = svgFrom(
        '<svg class="fcr-star" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" aria-hidden="true" focusable="false"><path d="' +
          STAR_PATH +
          '"></path></svg>'
      );
      if (index > rounded) {
        star.classList.add('fcr-star--empty');
      }
      wrap.appendChild(star);
    }

    return wrap;
  }

  function verifiedNode(style) {
    var wrap = el('span', 'fcr-card__verified');

    if (style === 'chip') {
      wrap.appendChild(textEl('span', 'fcr-card__verified-chip', 'Verified'));
    } else {
      /* Outlined, not filled: a knocked out tick disappears whenever the badge
         colour and the tick colour land on the same side of the surface. */
      wrap.appendChild(
        svgFrom(
          '<svg class="fcr-card__verified-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="9.2"></circle><path d="M8 12.4l2.7 2.7L16.2 9.5"></path></svg>'
        )
      );
      wrap.appendChild(textEl('span', 'visually-hidden', 'Verified buyer'));
    }

    return wrap;
  }

  function mediaNode(review, eager) {
    var videoUrl = safeMediaUrl(review.video_url);
    var imageUrl = hasImage(review) ? safeMediaUrl(review.image_urls[0]) : '';
    var media = el('div', 'fcr-card__media');
    var video;
    var image;

    if (videoUrl) {
      video = document.createElement('video');
      video.src = videoUrl;
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.preload = 'metadata';
      video.tabIndex = -1;
      if (imageUrl) {
        video.poster = imageUrl;
      }
      media.appendChild(video);
      media.appendChild(
        svgFrom(
          '<div class="fcr-card__play"><span><svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><polygon points="6 4 20 12 6 20"></polygon></svg></span></div>'
        )
      );
      return media;
    }

    if (!imageUrl) return null;

    image = document.createElement('img');
    image.src = imageUrl;
    image.alt = 'Photo from a review by ' + (review.author_name || 'a customer');
    image.decoding = 'async';
    image.loading = eager ? 'eager' : 'lazy';
    if (eager) {
      image.setAttribute('fetchpriority', 'high');
    }
    media.appendChild(image);

    return media;
  }

  function slideNode(review, context, index, total) {
    var settings = context.settings;
    var slide = el('div', 'fcr__slide');
    var card = el('article', 'fcr-card');
    var body = el('div', 'fcr-card__body');
    var who = el('div', 'fcr-card__who');
    var media = mediaNode(review, index < 2);
    var dateLabel = formatDate(review.date_timestamp, settings.fcrDateFormat, settings.fcrDateLocale);

    slide.setAttribute('role', 'group');
    slide.setAttribute('aria-roledescription', 'slide');
    slide.setAttribute('aria-label', index + 1 + ' of ' + total);
    slide.setAttribute('data-fcr-slide', '');
    slide.setAttribute('data-fcr-dynamic', 'true');
    slide.setAttribute('data-rating', formatAverage(review.rating));
    slide.setAttribute('data-verified', review.verified ? 'true' : 'false');

    if (media) {
      card.appendChild(media);
      if (settings.fcrStyle !== 'stacked') {
        card.appendChild(el('div', 'fcr-card__scrim'));
      }
    }

    if (toBool(settings.fcrShowStars)) {
      body.appendChild(starsNode(review.rating));
    }

    who.appendChild(textEl('span', 'fcr-card__name', review.author_name || 'Anonymous'));
    if (review.verified && toBool(settings.fcrShowVerified)) {
      who.appendChild(verifiedNode(settings.fcrVerifiedStyle));
    }
    body.appendChild(who);

    if (dateLabel) {
      body.appendChild(textEl('span', 'fcr-card__date', dateLabel));
    }

    if (toBool(settings.fcrShowText) && trim(review.review_text)) {
      body.appendChild(textEl('p', 'fcr-card__text', review.review_text));
    }

    if (toBool(settings.fcrShowItemType) && trim(review.item_type)) {
      body.appendChild(textEl('span', 'fcr-card__item', review.item_type));
    }

    card.appendChild(body);
    slide.appendChild(card);
    return slide;
  }

  /* ------------------------------------------------------------- carousel */

  function createCarousel(section) {
    var track = section.querySelector('[data-fcr-track]');
    var dotsWrap = section.querySelector('[data-fcr-dots]');
    var rail = section.querySelector('[data-fcr-rail]');
    var thumb = section.querySelector('[data-fcr-rail-thumb]');
    var prevButton = section.querySelector('[data-fcr-prev]');
    var nextButton = section.querySelector('[data-fcr-next]');
    var settings = section.dataset;
    var wrapAround = toBool(settings.fcrWrap);
    var focusMode = toBool(settings.fcrFocus);
    var dotsMax = toNumber(settings.fcrDotsMax, 12);
    var activeIndex = 0;
    var frame = null;
    var settleTimer = null;
    var resizeTimer = null;
    var autoplayTimer = null;
    var autoplayDelay = Math.max(2000, toNumber(settings.fcrAutoplayDelay, 5) * 1000);
    var autoplayEnabled = toBool(settings.fcrAutoplay) && !reducedMotion;
    var paused = false;
    var inView = true;
    var dragging = false;

    /* Loop state. realCount is the number of genuine reviews, realStart is
       where they begin in the cloned strip, setWidth is one full lap and
       basePosition is the scrollLeft that rests the first real card. */
    var looping = false;
    var realCount = 0;
    var realStart = 0;
    var setWidth = 0;
    var basePosition = 0;
    var carousel;

    function domSlides() {
      return Array.prototype.slice.call(track.querySelectorAll('[data-fcr-slide]'));
    }

    function realSlides() {
      return Array.prototype.slice.call(
        track.querySelectorAll('[data-fcr-slide]:not([data-fcr-clone])')
      );
    }

    function realIndexOf(slide) {
      var value = slide ? Number(slide.getAttribute('data-fcr-real')) : NaN;
      return Number.isFinite(value) ? value : 0;
    }

    function alignOffset(slideWidth) {
      if (settings.fcrAlign === 'center') {
        return (track.clientWidth - slideWidth) / 2;
      }
      return 0;
    }

    /* Where scrollLeft has to sit for this slide to be at rest. */
    function restPosition(slide) {
      var trackRect = track.getBoundingClientRect();
      var slideRect = slide.getBoundingClientRect();
      return track.scrollLeft + (slideRect.left - trackRect.left) - alignOffset(slideRect.width);
    }

    /* ------------------------------------------------------- cloned strip */

    function tagReals() {
      var reals = realSlides();

      realCount = reals.length;
      reals.forEach(function (slide, index) {
        slide.setAttribute('data-fcr-real', index);
      });

      return reals;
    }

    function cloneOf(slide) {
      var copy = slide.cloneNode(true);
      var image = copy.querySelector('img');
      var video = copy.querySelector('video');

      copy.setAttribute('data-fcr-clone', 'true');
      copy.setAttribute('aria-hidden', 'true');
      copy.classList.remove('is-active');
      copy.removeAttribute('id');
      /* Without this the theme editor treats a copy as a selectable block. */
      copy.removeAttribute('data-shopify-editor-block');

      if (image) {
        image.loading = 'lazy';
        image.removeAttribute('fetchpriority');
      }
      if (video) {
        video.preload = 'none';
        video.removeAttribute('autoplay');
      }

      return copy;
    }

    /* One lap, measured off the gap between two neighbouring cards so the
       column gap is included without reading it back out of the CSS. */
    function measureSet(reals) {
      var step;

      if (reals.length < 2) return 0;
      step = reals[1].getBoundingClientRect().left - reals[0].getBoundingClientRect().left;
      if (!(step > 0)) return 0;

      return step * reals.length;
    }

    function copiesNeeded() {
      if (!setWidth) return 0;
      return Math.max(2, Math.ceil(track.clientWidth / setWidth) + 1);
    }

    function buildClones() {
      var reals;
      var copies;
      var before = document.createDocumentFragment();
      var after = document.createDocumentFragment();
      var all;
      var lap;
      var index;

      removeAll(track.querySelectorAll('[data-fcr-clone]'));

      reals = tagReals();
      realStart = 0;
      looping = false;
      setWidth = 0;
      basePosition = 0;

      if (!wrapAround || realCount < 2) return;

      /* A lap shorter than the viewport would put the same review on screen
         twice at once, so the loop stays off and the slider just stops. */
      setWidth = measureSet(reals);
      if (!setWidth || setWidth < track.clientWidth - 1) {
        setWidth = 0;
        return;
      }

      copies = copiesNeeded();

      for (index = 0; index < copies; index += 1) {
        reals.forEach(function (slide) {
          before.appendChild(cloneOf(slide));
          after.appendChild(cloneOf(slide));
        });
      }

      track.insertBefore(before, reals[0]);
      track.appendChild(after);

      realStart = copies * realCount;
      looping = true;
      ensureJumpStyle();

      /* Re-measure against the built strip: exact beats extrapolated. */
      all = domSlides();
      if (all[realStart] && all[realStart + realCount]) {
        lap =
          all[realStart + realCount].getBoundingClientRect().left -
          all[realStart].getBoundingClientRect().left;
        if (lap > 0) {
          setWidth = lap;
        }
        basePosition = restPosition(all[realStart]);
      }
    }

    function remeasure() {
      var all;

      if (!looping) return;

      all = domSlides();
      if (!all[realStart] || !all[realStart + realCount]) return;

      setWidth =
        all[realStart + realCount].getBoundingClientRect().left -
        all[realStart].getBoundingClientRect().left;
      basePosition = restPosition(all[realStart]);
    }

    /* A hard reposition that snapping and smooth scrolling must not touch. */
    function jumpTo(position) {
      var snap = track.style.scrollSnapType;
      var behavior = track.style.scrollBehavior;

      track.style.scrollSnapType = 'none';
      track.style.scrollBehavior = 'auto';
      track.scrollLeft = position;
      /* Flush the reposition before snapping is handed back. */
      void track.offsetWidth;
      track.style.scrollSnapType = snap;
      track.style.scrollBehavior = behavior;
    }

    /* Shifts the strip back into the real set by whole laps. The picture is
       identical either side of the shift, which is what makes it invisible.
       Returns how far it moved so a drag in progress can follow it. */
    function normalize() {
      var list;
      var anchorIndex;
      var targetIndex;
      var laps;
      var delta;

      if (!looping || setWidth <= 0) return 0;

      /* Which lap of the strip the track is currently resting on. */
      laps = Math.floor((track.scrollLeft - basePosition + 0.5) / setWidth);
      if (!laps) return 0;

      list = domSlides();
      anchorIndex = nearestDomIndex(list);
      targetIndex = anchorIndex - laps * realCount;

      if (!list[targetIndex]) return 0;

      /* Measured across the two cards that trade places rather than off the lap
         arithmetic. A subpixel error is enough for mandatory snapping to nudge
         the track once snapping is handed back, which reads as a shudder. */
      delta = restPosition(list[anchorIndex]) - restPosition(list[targetIndex]);
      if (!delta) return 0;

      /* The whole swap has to finish inside one frame: jump, restate every card
         for its new position, flush, then let transitions back in. Leaving the
         repaint to the next animation frame puts one frame of the outgoing
         card's scale on the incoming one, which is the shake. */
      track.classList.add('is-jumping');
      jumpTo(track.scrollLeft - delta);
      paint();
      void track.offsetWidth;
      track.classList.remove('is-jumping');

      return -delta;
    }

    /* ---------------------------------------------------------- navigation */

    function goToDom(domIndex, behavior) {
      var list = domSlides();
      var target;

      if (!list.length) return;

      domIndex = clamp(domIndex, 0, list.length - 1);
      target = list[domIndex];

      track.scrollTo({
        left: restPosition(target),
        behavior: behavior || (reducedMotion ? 'auto' : 'smooth')
      });

      /* Commit the target straight away. Scroll events are dispatched on the
         frame cycle, so waiting for one to repaint would let two quick arrow
         clicks both read the same stale index and land on the same slide. */
      activeIndex = realIndexOf(target);
      paintActive(list, domIndex);
      paintDots(activeIndex);
      paintArrows(activeIndex, realCount);
    }

    /* Takes a real review index and, when looping, travels to whichever copy
       of it is closest. That is the whole trick: from the last card, the
       nearest copy of review one is the one immediately to the right. */
    function goTo(index, behavior) {
      var list;
      var current;
      var best = -1;
      var bestDistance = Infinity;

      if (!realCount) return;

      if (!looping) {
        if (wrapAround) {
          index = mod(index, realCount);
        } else {
          index = clamp(index, 0, realCount - 1);
        }
        goToDom(index, behavior);
        return;
      }

      index = mod(index, realCount);

      /* Normalising first keeps "closest copy" measured from the real set,
         including on the very first call, when the strip still sits at zero. */
      normalize();

      list = domSlides();
      current = nearestDomIndex(list);

      list.forEach(function (slide, domIndex) {
        var distance;
        if (realIndexOf(slide) !== index) return;
        distance = Math.abs(domIndex - current);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = domIndex;
        }
      });

      goToDom(best < 0 ? realStart + index : best, behavior);
    }

    /* The active slide is whichever one is closest to the resting position a
       snap would put it in: the track centre when centred, the left edge when
       left aligned. Measuring from the centre in both modes made a left
       aligned row report the middle visible card as active. */
    function anchorPoint() {
      var trackRect = track.getBoundingClientRect();
      if (settings.fcrAlign === 'center') {
        return trackRect.left + track.clientWidth / 2;
      }
      return trackRect.left;
    }

    function slidePoint(rect) {
      if (settings.fcrAlign === 'center') {
        return rect.left + rect.width / 2;
      }
      return rect.left;
    }

    function nearestDomIndex(list) {
      var anchor = anchorPoint();
      var best = 0;
      var bestDistance = Infinity;

      (list || domSlides()).forEach(function (slide, index) {
        var distance = Math.abs(slidePoint(slide.getBoundingClientRect()) - anchor);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = index;
        }
      });

      return best;
    }

    /* ------------------------------------------------------------ painting */

    /* Falloff spans two slides so cards step down in size the further they sit
       from the middle, rather than every off-centre card sharing one size. */
    function paintProximity(list, trackCenter) {
      list.forEach(function (slide) {
        var rect = slide.getBoundingClientRect();
        var distance = Math.abs(rect.left + rect.width / 2 - trackCenter);
        var proximity = clamp(1 - distance / Math.max(1, rect.width * FOCUS_FALLOFF), 0, 1);
        slide.style.setProperty('--fcr-prox', proximity.toFixed(3));
      });
    }

    function paintRail() {
      var railWidth = rail ? rail.clientWidth : 0;
      var maxScroll;
      var ratio;
      var thumbRatio;
      var thumbWidth;

      if (!rail || !thumb || !railWidth) return;

      if (looping && setWidth > 0) {
        /* An endless track has no end to measure against, so the rail reports
           position within one lap and starts over each time round. */
        ratio = mod(track.scrollLeft - basePosition, setWidth) / setWidth;
        thumbRatio = clamp(track.clientWidth / setWidth, 0.12, 1);
      } else {
        maxScroll = track.scrollWidth - track.clientWidth;
        ratio = maxScroll > 0 ? clamp(track.scrollLeft / maxScroll, 0, 1) : 0;
        thumbRatio = track.scrollWidth > 0 ? clamp(track.clientWidth / track.scrollWidth, 0.12, 1) : 1;
      }

      thumbWidth = railWidth * thumbRatio;
      thumb.style.width = thumbRatio * 100 + '%';
      thumb.style.transform = 'translateX(' + (railWidth - thumbWidth) * ratio + 'px)';
    }

    function paintDots(index) {
      if (!dotsWrap) return;
      Array.prototype.slice.call(dotsWrap.children).forEach(function (dot, dotIndex) {
        var isActive = dotIndex === index;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }

    function paintArrows(index, total) {
      if (!prevButton || !nextButton) return;
      if (looping || wrapAround || total < 2) {
        prevButton.disabled = total < 2;
        nextButton.disabled = total < 2;
        return;
      }
      prevButton.disabled = index <= 0;
      nextButton.disabled = index >= total - 1;
    }

    /* Marks the centred slide so its photo can hold the zoom. While looping,
       every copy of that review is marked rather than only the one under the
       anchor. They then run their zoom in phase, so when a lap change trades
       one copy for another the photo is already at the same point in its zoom
       instead of starting over. The other copies sit a full lap away, which
       the loop guarantees is off screen. */
    function paintActive(list, domIndex) {
      var activeReal = list[domIndex] ? realIndexOf(list[domIndex]) : -1;

      list.forEach(function (slide, slideIndex) {
        var isActive = looping ? realIndexOf(slide) === activeReal : slideIndex === domIndex;
        slide.classList.toggle('is-active', isActive);
      });
    }

    function paint() {
      var list = domSlides();
      var domIndex;

      if (!list.length) return;

      domIndex = nearestDomIndex(list);
      activeIndex = realIndexOf(list[domIndex]);

      if (focusMode) {
        /* Dimming always reads from the centre, whatever the snap alignment. */
        paintProximity(list, track.getBoundingClientRect().left + track.clientWidth / 2);
      }

      paintActive(list, domIndex);
      paintRail();
      paintDots(activeIndex);
      paintArrows(activeIndex, realCount);
    }

    function schedulePaint() {
      if (frame) return;
      frame = window.requestAnimationFrame(function () {
        frame = null;
        paint();
      });
    }

    /* Recentring mid-flick would kill iOS momentum, so it waits for quiet. */
    function scheduleSettle() {
      if (!looping) return;
      if (settleTimer) {
        window.clearTimeout(settleTimer);
      }
      settleTimer = window.setTimeout(function () {
        settleTimer = null;
        if (dragging) return;
        /* normalize repaints inside its own frozen frame. */
        normalize();
      }, SETTLE_DELAY);
    }

    function buildDots() {
      if (!dotsWrap) return;

      empty(dotsWrap);

      if (realCount < 2 || realCount > dotsMax) {
        dotsWrap.hidden = true;
        return;
      }

      dotsWrap.hidden = false;

      realSlides().forEach(function (slide, index) {
        var dot = el('button', 'fcr__dot');
        dot.type = 'button';
        dot.setAttribute('aria-label', 'Go to review ' + (index + 1));
        dot.addEventListener('click', function () {
          stopAutoplay();
          goTo(index);
        });
        dotsWrap.appendChild(dot);
      });
    }

    /* Percentage widths mean a lap changes length with the viewport. */
    function relayout() {
      var index = activeIndex;
      var copies = looping && realCount ? realStart / realCount : 0;

      remeasure();

      if (!looping || setWidth < track.clientWidth - 1 || copiesNeeded() > copies) {
        buildClones();
      }

      goTo(index, 'auto');
      schedulePaint();
    }

    function startAutoplay() {
      if (!autoplayEnabled || autoplayTimer) return;
      autoplayTimer = window.setInterval(function () {
        if (paused || !inView || document.hidden || realCount < 2) return;
        if (!looping && !wrapAround && activeIndex >= realCount - 1) {
          goTo(0);
          return;
        }
        goTo(activeIndex + 1);
      }, autoplayDelay);
    }

    function stopAutoplay() {
      if (!autoplayTimer) return;
      window.clearInterval(autoplayTimer);
      autoplayTimer = null;
    }

    /* Pointer drag on desktop. Touch already scrolls natively. */
    function bindDrag() {
      var moved = false;
      var startX = 0;
      var startScroll = 0;
      var releasedAt = 0;

      track.addEventListener('pointerdown', function (event) {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        dragging = true;
        moved = false;
        startX = event.clientX;
        startScroll = track.scrollLeft;
      });

      track.addEventListener('pointermove', function (event) {
        var delta;
        var shift;
        if (!dragging) return;
        delta = event.clientX - startX;
        if (!moved && Math.abs(delta) < 5) return;
        if (!moved) {
          moved = true;
          track.classList.add('is-dragging');
          track.setPointerCapture(event.pointerId);
        }
        track.scrollLeft = startScroll - delta;
        /* scrollLeft is ours during a drag, so the strip can be recentred on
           the spot as long as the drag origin moves with it. */
        shift = normalize();
        if (shift) {
          startScroll += shift;
        }
        schedulePaint();
      });

      function endDrag(event) {
        if (!dragging) return;
        dragging = false;
        if (!moved) return;
        releasedAt = Date.now();
        track.classList.remove('is-dragging');
        if (event && event.pointerId != null && track.hasPointerCapture(event.pointerId)) {
          track.releasePointerCapture(event.pointerId);
        }
        goToDom(nearestDomIndex());
      }

      track.addEventListener('pointerup', endDrag);
      track.addEventListener('pointercancel', endDrag);
      /* A release outside the track still has to end the gesture. */
      window.addEventListener('pointerup', endDrag);

      track.addEventListener(
        'click',
        function (event) {
          if (Date.now() - releasedAt < 180) {
            event.preventDefault();
            event.stopPropagation();
          }
        },
        true
      );
    }

    track.addEventListener(
      'scroll',
      function () {
        schedulePaint();
        scheduleSettle();
      },
      { passive: true }
    );

    window.addEventListener('resize', function () {
      schedulePaint();
      if (resizeTimer) {
        window.clearTimeout(resizeTimer);
      }
      resizeTimer = window.setTimeout(function () {
        resizeTimer = null;
        relayout();
      }, 180);
    });

    if (prevButton) {
      prevButton.addEventListener('click', function () {
        stopAutoplay();
        goTo(activeIndex - 1);
      });
    }

    if (nextButton) {
      nextButton.addEventListener('click', function () {
        stopAutoplay();
        goTo(activeIndex + 1);
      });
    }

    track.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        stopAutoplay();
        goTo(activeIndex + 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        stopAutoplay();
        goTo(activeIndex - 1);
      }
    });

    section.addEventListener('pointerenter', function () {
      paused = true;
    });

    section.addEventListener('pointerleave', function () {
      paused = false;
    });

    section.addEventListener('focusin', function () {
      paused = true;
    });

    section.addEventListener('focusout', function () {
      paused = false;
    });

    if (window.IntersectionObserver) {
      new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            inView = entry.isIntersecting;
          });
        },
        { threshold: 0.2 }
      ).observe(section);
    }

    bindDrag();
    tagReals();

    carousel = {
      section: section,
      track: track,
      slides: realSlides,
      goTo: goTo,
      realIndexOf: realIndexOf,
      isLooping: function () {
        return looping;
      },
      refresh: function () {
        buildClones();
        buildDots();
        schedulePaint();
        startAutoplay();
      },
      destroy: function () {
        stopAutoplay();
        if (frame) {
          window.cancelAnimationFrame(frame);
          frame = null;
        }
        if (settleTimer) {
          window.clearTimeout(settleTimer);
          settleTimer = null;
        }
        if (resizeTimer) {
          window.clearTimeout(resizeTimer);
          resizeTimer = null;
        }
      }
    };

    section.fcrCarousel = carousel;
    return carousel;
  }

  /* -------------------------------------------------------------- summary */

  function renderSummary(section, reviews) {
    var wrap = section.querySelector('[data-fcr-rating]');
    var scoreNode = section.querySelector('[data-fcr-score]');
    var starsHost = section.querySelector('[data-fcr-summary-stars]');
    var countNode = section.querySelector('[data-fcr-count]');
    var settings = section.dataset;
    var cache = window.CustomerReviewsCache;
    var summary;

    if (!wrap || !cache || typeof cache.getSummary !== 'function') return;

    summary = cache.getSummary(reviews);

    if (!summary.count) {
      wrap.hidden = true;
      return;
    }

    wrap.hidden = false;

    if (scoreNode && toBool(settings.fcrShowScore)) {
      scoreNode.textContent = formatAverage(summary.average);
      scoreNode.hidden = false;
    }

    if (starsHost && toBool(settings.fcrShowSummaryStars)) {
      empty(starsHost);
      starsHost.appendChild(starsNode(summary.average));
      starsHost.hidden = false;
    }

    if (countNode && toBool(settings.fcrShowCount)) {
      countNode.textContent = summary.count + ' review' + (summary.count === 1 ? '' : 's');
      countNode.hidden = false;
    }
  }

  /* ------------------------------------------------------------------ init */

  function setState(section, state) {
    Array.prototype.slice.call(section.querySelectorAll('[data-fcr-state]')).forEach(function (node) {
      node.hidden = node.getAttribute('data-fcr-state') !== state;
    });
  }

  function renderSlides(section, reviews) {
    var track = section.querySelector('[data-fcr-track]');
    var settings = section.dataset;
    var existing;
    var total;

    if (!track) return 0;

    /* Loop copies are rebuilt from whatever is left, so they go first. */
    removeAll(track.querySelectorAll('[data-fcr-clone]'));
    removeAll(track.querySelectorAll('[data-fcr-dynamic]'));

    existing = track.querySelectorAll('[data-fcr-slide]').length;
    total = existing + reviews.length;

    reviews.forEach(function (review, index) {
      track.appendChild(
        slideNode(review, { settings: settings }, existing + index, total)
      );
    });

    Array.prototype.slice.call(track.querySelectorAll('[data-fcr-slide]')).forEach(function (slide, index) {
      slide.setAttribute('aria-label', index + 1 + ' of ' + total);
    });

    return total;
  }

  /* The theme has no global smooth scrolling, so the handoff to the full
     reviews section is animated here instead. */
  function bindJumpLink(section) {
    var link = section.querySelector('[data-fcr-jump]');
    var href;

    if (!link) return;

    href = link.getAttribute('href') || '';
    if (href.charAt(0) !== '#' || href.length < 2) return;

    link.addEventListener('click', function (event) {
      var target = document.getElementById(href.slice(1));
      if (!target) return;

      event.preventDefault();
      target.scrollIntoView({
        behavior: reducedMotion ? 'auto' : 'smooth',
        block: 'start'
      });
      if (window.history && window.history.replaceState) {
        window.history.replaceState(null, '', href);
      }
    });
  }

  /* A centred carousel resting on slide one leaves its whole left half empty,
     because there is nothing before the first card. Opening on a middle slide
     gives the focused card neighbours on both sides. A looping strip already
     has cards on both sides, so it can open where the reviews really start. */
  function openingIndex(section, total) {
    var carousel = section.fcrCarousel;

    if (carousel && carousel.isLooping()) return 0;
    if (section.dataset.fcrStart !== 'middle' || total < 3) return 0;
    if (section.dataset.fcrAlign !== 'center') return 0;
    return Math.floor((total - 1) / 2);
  }

  function initSection(section) {
    var settings = section.dataset;
    var mode = settings.fcrMode || 'curated';
    var carousel;
    var staticSlides;

    if (section.dataset.fcrInitialized === 'true') return;
    section.dataset.fcrInitialized = 'true';

    bindJumpLink(section);
    carousel = createCarousel(section);
    staticSlides = carousel.slides().length;

    /* Blocks only: everything is already server rendered. */
    if (mode === 'blocks') {
      if (!staticSlides) {
        setState(section, 'empty');
        return;
      }
      setState(section, 'ready');
      carousel.refresh();
      carousel.goTo(openingIndex(section, staticSlides), 'auto');

      if (toBool(settings.fcrShowRating)) {
        whenDataLayerReady()
          .then(function (cache) {
            return cache.load(settings.fcrCsvUrl).then(function (reviews) {
              renderSummary(
                section,
                cache.filterReviews(reviews, {
                  reviewSource: settings.fcrReviewSource,
                  productHandle: settings.fcrProductHandle,
                  productHandleOverride: settings.fcrProductHandleOverride,
                  includeGlobalReviews: settings.fcrIncludeGlobal
                })
              );
            });
          })
          .catch(function () {
            /* Summary is optional decoration for block driven sliders. */
          });
      }
      return;
    }

    /* Manual slides are already painted, so never blank them for a skeleton. */
    if (!staticSlides) {
      setState(section, 'loading');
    }

    whenDataLayerReady()
      .then(function (cache) {
        return cache.load(settings.fcrCsvUrl).then(function (reviews) {
          var scoped = cache.filterReviews(reviews, {
            reviewSource: settings.fcrReviewSource,
            productHandle: settings.fcrProductHandle,
            productHandleOverride: settings.fcrProductHandleOverride,
            includeGlobalReviews: settings.fcrIncludeGlobal
          });
          var featured = selectReviews(scoped, settings);
          var total = renderSlides(section, featured);

          if (!total) {
            setState(section, 'empty');
            return;
          }

          if (toBool(settings.fcrShowRating)) {
            renderSummary(section, scoped);
          }

          setState(section, 'ready');
          carousel.refresh();
          carousel.goTo(openingIndex(section, total), 'auto');
        });
      })
      .catch(function (error) {
        if (window.console && console.error) {
          console.error('[Featured customer reviews]', error);
        }
        setState(section, staticSlides ? 'ready' : 'error');
        if (staticSlides) {
          carousel.refresh();
        }
      });
  }

  function initAll(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    var sections = root.matches && root.matches('[data-fcr-section]')
      ? [root]
      : Array.prototype.slice.call(root.querySelectorAll('[data-fcr-section]'));

    sections.forEach(initSection);
  }

  window.FeaturedCustomerReviews = {
    initAll: initAll,
    initSection: initSection
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    Array.prototype.slice.call(event.target.querySelectorAll('[data-fcr-section]')).forEach(function (section) {
      if (section.fcrCarousel) {
        section.fcrCarousel.destroy();
      }
    });
  });

  document.addEventListener('shopify:block:select', function (event) {
    var slide = event.target.closest ? event.target.closest('[data-fcr-slide]') : null;
    var section = event.target.closest ? event.target.closest('[data-fcr-section]') : null;
    var carousel = section && section.fcrCarousel;

    if (!slide || !carousel) return;
    carousel.goTo(carousel.realIndexOf(slide));
  });
}());